import React from "react";
import { Button } from "@UI/Components/Button/Button";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import { CandidateOfferModal } from "./ServiceOfferModal";
import { IServiceOfferModalWithButtonProps } from "../types";
import { useDataQueryClient } from "@Libs/DataLayer/hooks/useDataQuery";
import { DeclineOfferModal } from "./CandidatesSection/DeclineOffer/DeclineOfferModal";

import { HStack } from "@UI/Components/Stack/Stack";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Text } from "@UI/Components/Text/Text";
import { Xmark } from "@UI/Components/Icons/Xmark";

import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { CandidateOfferPrice } from "./CandidatesSection/CandidateOfferPrice";
import { useServiceRequestFlow } from "@modules/ServiceRequest/hooks/useServiceRequestFlow";

export const ServiceOfferModalWithButton = (
  props: IServiceOfferModalWithButtonProps
) => {
  const toast = useToast();
  const { isMobile } = useMediaQuery();
  const { serviceRequest } = useServiceRequestFlow();
  const isActivationRateEnabled = useFeatureFlag("new-activation-rate");

  const { serviceOffer: serviceOfferData } = useServiceOffer(
    props.candidate.serviceOffer?.id,
    { includes: ["expenses"] }
  );
  const { currentUser } = useCurrentUser();
  const isSubscribed = currentUser?.subscribed ?? false;

  const showOfferPrice = isActivationRateEnabled && !!serviceOfferData;

  // Sanitize the HTML to allow only <span> elements. The rest will be removed automatically.
  // This will remove unsafe code from totalPrice like executable tags <script>
  const sanitizedHTML =
    props.totalPrice && props.totalPrice.replace(/<(?!\/?span\b)[^>]*>/gi, "");

  const {
    isOpen: isCandidateOfferModalOpen,
    onOpen: onCandidateOfferModalOpen,
    onClose: onCandidateOfferModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeclineOfferModalOpen,
    onOpen: onDeclineOfferModalOpen,
    onClose: onDeclineOfferModalClose,
  } = useDisclosure();

  const selectedCandidate = props.candidate;

  const queryClient = useDataQueryClient();

  const handelDeclineOfferModalOpen = () => {
    onCandidateOfferModalClose();
    onDeclineOfferModalOpen();
  };

  const removeCandidateFromList = () => {
    const existingData = queryClient.getQueryData("serviceRequestCandidates");
    Array.isArray(existingData) &&
      queryClient.setQueryData(
        "serviceRequestCandidates",
        existingData.filter(
          (item) => item.conversationId !== selectedCandidate.conversationId
        )
      );
  };

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  const billingUnit = props.candidate.serviceOffer?.billingUnit;
  const isEstimate = props.candidate.serviceOffer?.rateType === "estimate";
  const showPriceButton = showOfferPrice || (!isActivationRateEnabled && !isEstimate && !!props.totalPrice);

  return (
    <>
      <HStack spacing="2" alignItems="center" w="full">
        {isEstimate && !isActivationRateEnabled && (
          <Text fontWeight="bold" fontSize="sm" color="gray.800" m="0" whiteSpace="nowrap" flexShrink={0}>
            <FormattedMessage {...messages["serviceRequest.offerDetail.visitForQuote"]} />
          </Text>
        )}
        <Button
          data-testid="view-offer-modal"
          {...(showPriceButton
            ? {
              variant: "unstyled",
              w: "fit-content",
              height: "fit-content",
              textAlign: "right",
              fontSize: "lg",
              mb: "4px",
            }
            : { w: isEstimate ? undefined : "full", size: props.buttonSize || "lg" })}
          colorScheme="seaGreen"
          data-bs-dismiss="offcanvas" // this will close the legacy profile detail modal or legacy conversation modal
          onClick={onCandidateOfferModalOpen}
        >
          {showPriceButton ? (
            showOfferPrice ? (
              <CandidateOfferPrice
                serviceOffer={serviceOfferData}
                isSubscribed={isSubscribed}
              />
            ) : (
              // if totalPrice is available, it means we are displaying a SP's price offer with underlined text
              // if not we are displaying see offer button.
              <Box
                // used dangerouslySetInnerHTML because totalPrice contains <span>
                dangerouslySetInnerHTML={{ __html: sanitizedHTML }} // since its sanitized, it's safe :)
                fontWeight="700"
                color="gray.800"
              />
            )
          ) : (
            <FormattedMessage {...messages["serviceRequest.seeOffer"]} />
          )}
        </Button>
      </HStack>
      {!isActivationRateEnabled && !isEstimate && billingUnit && (
        <div className="text-gray-700 text-xs text-md-sm text-nowrap">
          <FormattedMessage {...messages[billingUnit]} />
          {props.candidate.serviceOffer?.offersFreeEstimation && (
            <div className="fw-bold">
              <FormattedMessage {...messages["serviceRequest.offerDetail.freeVisit"]} />
            </div>
          )}
        </div>
      )}
      <CandidateOfferModal
        candidate={selectedCandidate}
        isOpen={isCandidateOfferModalOpen}
        onClose={onCandidateOfferModalClose}
        onDeclineOfferModalOpen={() => handelDeclineOfferModalOpen()}
      />
      <DeclineOfferModal
        isOpen={isDeclineOfferModalOpen}
        onClose={onDeclineOfferModalClose}
        conversationId={selectedCandidate?.conversationId}
        serviceProviderName={selectedCandidate?.firstName || ""}
        isOfferMade={!!selectedCandidate?.serviceOffer}
        isCreatedBySP={serviceRequest?.authorId && serviceRequest?.authorId !== serviceRequest?.userId}
        showToast={showToast}
        removeCandidateFromList={removeCandidateFromList}
      />
    </>
  );
};
